// Self-contained vanilla-JS animations for the V1 marketing homepage.
//
// Used in two places:
//   1. The React route /www_v1 — IndexPageV1.jsx imports this for its
//      side effect (defining window.__iv1RunAnimations) and calls the
//      function from a useEffect once the DOM is mounted.
//   2. The static snapshot at lf_fe/public/www/index.html — scripts/
//      save-www-page.sh inlines this file's contents into the saved
//      HTML so the animations run on the deployed marketing page.
//
// Keep this file dependency-free and runnable as an inline <script>
// (no imports, no JSX, no ES module exports).

(function () {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  function runProgressChart() {
    var container = document.querySelector('[data-iv1-anim="progress-chart"]');
    if (!container || container.__iv1AnimSetup) return;
    container.__iv1AnimSetup = true;

    var clipRect = container.querySelector(
      '[data-iv1-anim="progress-chart-clip"]',
    );
    var label = container.querySelector(
      '[data-iv1-anim="progress-chart-label"]',
    );
    var widthAttr = container.getAttribute("data-iv1-chart-width");
    var innerW = widthAttr ? Number(widthAttr) : 0;
    if (!clipRect || !innerW) return;

    function draw() {
      // Setting CSS width (not the SVG attribute) so the inline
      // `transition: width …` on the rect actually fires.
      clipRect.style.width = innerW + "px";
      if (label) label.style.opacity = "1";
    }

    if (typeof IntersectionObserver === "undefined") {
      draw();
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (entries[i].isIntersecting) {
            observer.disconnect();
            draw();
            return;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(container);
  }

  function runAccountsBubbles() {
    var container = document.querySelector(
      '[data-iv1-anim="accounts-preview"]',
    );
    if (!container || container.__iv1AnimSetup) return;
    container.__iv1AnimSetup = true;

    var bubbles = Array.prototype.slice.call(
      container.querySelectorAll('[data-iv1-anim="account-bubble"]'),
    );
    if (!bubbles.length) return;

    bubbles.forEach(function (b) {
      b.style.opacity = "0";
    });

    function start() {
      var configs = bubbles.map(function () {
        return {
          delayMs: Math.floor(Math.random() * 1000),
          durationMs: 3300 + Math.floor(Math.random() * 1001),
          gravity: 1550 + Math.random() * 760,
          restitution: 0.38 + Math.random() * 0.28,
          bounceDamping: 0.88 + Math.random() * 0.1,
          fallDistanceRatio: 0.7 + Math.random() * 0.26,
        };
      });
      var viewportHeight = window.innerHeight || 900;
      var sims = bubbles
        .map(function (el, i) {
          var c = configs[i];
          var sim = {
            element: el,
            delayMs: c.delayMs,
            durationMs: c.durationMs,
            gravity: c.gravity,
            restitution: c.restitution,
            bounceDamping: c.bounceDamping,
            y: -Math.max(280, Math.round(viewportHeight * c.fallDistanceRatio)),
            vy: 0,
            bounceCount: 0,
            lastTime: null,
            done: false,
          };
          el.style.opacity = "0";
          el.style.transform = "translate3d(0, " + sim.y + "px, 0)";
          return sim;
        })
        .filter(Boolean);

      if (!sims.length) return;

      var startTime = performance.now();

      function tick(now) {
        var allDone = true;
        for (var i = 0; i < sims.length; i += 1) {
          var sim = sims[i];
          if (sim.done) continue;
          allDone = false;

          var elapsed = now - startTime;
          if (elapsed < sim.delayMs) continue;

          var localElapsed = elapsed - sim.delayMs;
          if (localElapsed >= sim.durationMs) {
            sim.done = true;
            sim.element.style.opacity = "1";
            sim.element.style.transform = "translate3d(0, 0px, 0)";
            continue;
          }

          var rawDt = sim.lastTime
            ? Math.min((now - sim.lastTime) / 1000, 0.04)
            : 0;
          sim.lastTime = now;

          if (rawDt > 0) {
            var substep = 1 / 120;
            var steps = Math.max(1, Math.ceil(rawDt / substep));
            var dt = rawDt / steps;
            for (var s = 0; s < steps; s += 1) {
              sim.vy += sim.gravity * dt;
              sim.y += sim.vy * dt;
              if (sim.y > 0) {
                sim.y = 0;
                sim.vy =
                  -Math.abs(sim.vy) *
                  sim.restitution *
                  Math.pow(sim.bounceDamping, sim.bounceCount);
                sim.bounceCount += 1;
              }
            }
          }

          if (
            localElapsed > sim.durationMs * 0.62 &&
            Math.abs(sim.vy) < 22 &&
            Math.abs(sim.y) < 0.9
          ) {
            sim.done = true;
            sim.element.style.opacity = "1";
            sim.element.style.transform = "translate3d(0, 0px, 0)";
            continue;
          }

          var fadeProgress = Math.min(1, localElapsed / 180);
          sim.element.style.opacity = fadeProgress.toFixed(3);
          sim.element.style.transform =
            "translate3d(0, " + Math.min(0, sim.y).toFixed(2) + "px, 0)";
        }
        if (!allDone) requestAnimationFrame(tick);
      }

      requestAnimationFrame(tick);
    }

    if (typeof IntersectionObserver === "undefined") {
      start();
      return;
    }
    var hasStarted = false;
    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i += 1) {
          if (entries[i].isIntersecting && !hasStarted) {
            hasStarted = true;
            observer.disconnect();
            start();
            return;
          }
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(container);
  }

  function run() {
    runProgressChart();
    runAccountsBubbles();
  }

  window.__iv1RunAnimations = run;
})();
